import * as THREE from 'three';
import type { Vec3 } from '../../sim/mathx';

export interface SweepIndicatorConfig {
  mountEnu: Vec3;
  mountYaw: number;
  rangeMin: number;
  rangeMax: number;
  scanPeriod: number;
}

/** A render-only sweep rotating once per configured scan period. */
export class SweepIndicator extends THREE.Group {
  readonly line: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  currentSweepAzimuth = 0;

  private readonly mountYaw: number;
  private readonly scanPeriod: number;
  private disposed = false;

  constructor(config: SweepIndicatorConfig) {
    super();
    if (!Number.isFinite(config.scanPeriod) || config.scanPeriod <= 0) {
      throw new RangeError('SweepIndicator scanPeriod must be finite and > 0');
    }
    this.name = 'radar-viz:sweep';
    this.mountYaw = config.mountYaw;
    this.scanPeriod = config.scanPeriod;
    this.position.set(config.mountEnu.x, config.mountEnu.y, config.mountEnu.z + 0.04);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute([config.rangeMin, 0, 0, config.rangeMax, 0, 0], 3),
    );
    this.line = new THREE.Line(
      geometry,
      new THREE.LineBasicMaterial({ color: 0x62e6ff, transparent: true, opacity: 0.8 }),
    );
    this.line.name = 'radar-viz:sweep-line';
    this.line.frustumCulled = false;
    this.add(this.line);
    this.applyAzimuth();
  }

  update(dtSeconds: number): void {
    const dt = Number.isFinite(dtSeconds) && dtSeconds > 0 ? dtSeconds : 0;
    this.currentSweepAzimuth =
      (this.currentSweepAzimuth + (Math.PI * 2 * dt) / this.scanPeriod) % (Math.PI * 2);
    this.applyAzimuth();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeFromParent();
    this.line.geometry.dispose();
    this.line.material.dispose();
    this.clear();
  }

  private applyAzimuth(): void {
    this.line.rotation.z = this.mountYaw + this.currentSweepAzimuth;
  }
}
